import { meshPickTargetsAtom, pickedPointAtom, pointCloudTargetsAtom } from '@/atoms';
import { useThree } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';
import * as THREE from 'three';

const DRAG_THRESHOLD = 5;

/**
 * Unified right-click picker for the 3D scene.
 * Raycasts against both point clouds (via Potree pick) and mesh targets
 * (IFC, LandXML, Fragments, etc.) and writes the closest hit to pickedPointAtom
 * so the shared ViewerContextMenu appears.
 */
export const SceneContextMenuPicker = () => {
  const { camera, gl } = useThree();
  const meshTargets = useAtomValue(meshPickTargetsAtom);
  const pointCloudTargets = useAtomValue(pointCloudTargetsAtom);
  const setPickedPoint = useSetAtom(pickedPointAtom);
  const raycaster = useMemo(() => new THREE.Raycaster(), []);

  useEffect(() => {
    const canvas = gl.domElement;
    let mouseDownPos: { x: number; y: number } | null = null;

    const handleMouseDown = (e: MouseEvent) => {
      if (e.button === 2) {
        mouseDownPos = { x: e.clientX, y: e.clientY };
      }
    };

    const handleMouseUp = async (e: MouseEvent) => {
      if (e.button !== 2 || !mouseDownPos) return;

      const dx = e.clientX - mouseDownPos.x;
      const dy = e.clientY - mouseDownPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      mouseDownPos = null;

      if (dist >= DRAG_THRESHOLD) return;

      const rect = canvas.getBoundingClientRect();
      const ndcX = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ndcY = -((e.clientY - rect.top) / rect.height) * 2 + 1;

      raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);

      // --- Point cloud picking ---
      let pcHit: THREE.Vector3 | null = null;
      let pcAttributes: Record<string, number | number[]> | undefined;
      let pcLayerId: string | undefined;
      if (pointCloudTargets.length > 0) {
        const hits = pointCloudTargets
          .map((pco) => pco.pick(gl, camera, raycaster.ray))
          .filter(Boolean);
        if (hits.length > 0 && hits[0]?.position) {
          const hit = hits[0]!;
          pcHit = hit.position!.clone();
          // Copy non-position/normal numeric attributes (e.g. Deviation,
          // intensity, classification) into a plain dict so the context
          // menu can render them. The picker writes itemSize-1 attribs as
          // bare numbers and N-vector attribs as number[].
          const attrs: Record<string, number | number[]> = {};
          for (const k of Object.keys(hit)) {
            if (k === 'position' || k === 'normal' || k === 'pointCloud') continue;
            const v = (hit as Record<string, unknown>)[k];
            if (typeof v === 'number' || Array.isArray(v)) {
              attrs[k] = v as number | number[];
            }
          }
          pcAttributes = Object.keys(attrs).length ? attrs : undefined;
          const ud = (hit.pointCloud as THREE.Object3D | undefined)?.userData;
          pcLayerId = (ud?.layerId as string | undefined) ?? undefined;
        }
      }

      // --- Mesh picking ---
      let meshHit: THREE.Vector3 | null = null;
      if (meshTargets.length > 0) {
        try {
          const prevLineThreshold = raycaster.params.Line.threshold;
          raycaster.params.Line.threshold = 0.1;
          const intersections = raycaster.intersectObjects(meshTargets, true);
          raycaster.params.Line.threshold = prevLineThreshold;
          if (intersections.length > 0) {
            meshHit = intersections[0].point.clone();
          }
        } catch {
          // Fragment LOD meshes may throw on raycast — skip silently
        }
      }

      // --- Fragment model picking (ThatOpen native raycast) ---
      let fragmentHit: THREE.Vector3 | null = null;
      try {
        const fragments = await getFragments();
        if (fragments && fragments.models.list.size > 0) {
          const mouse = new THREE.Vector2(e.clientX, e.clientY);
          for (const [, model] of fragments.models.list) {
            try {
              const result = await model.raycast({
                camera: camera as THREE.PerspectiveCamera,
                mouse,
                dom: canvas,
              });
              if (result) {
                const d = raycaster.ray.origin.distanceTo(result.point);
                if (!fragmentHit || d < raycaster.ray.origin.distanceTo(fragmentHit)) {
                  fragmentHit = result.point.clone();
                }
              }
            } catch {
              // model may not support raycasting
            }
          }
        }
      } catch {
        // fragments not initialized
      }

      // Pick the closest hit to the camera
      const origin = raycaster.ray.origin;
      let bestPoint: THREE.Vector3 | null = null;
      let bestDist = Infinity;

      if (pcHit) {
        const d = origin.distanceTo(pcHit);
        if (d < bestDist) {
          bestDist = d;
          bestPoint = pcHit;
        }
      }
      if (meshHit) {
        const d = origin.distanceTo(meshHit);
        if (d < bestDist) {
          bestDist = d;
          bestPoint = meshHit;
        }
      }
      if (fragmentHit) {
        const d = origin.distanceTo(fragmentHit);
        if (d < bestDist) {
          bestDist = d;
          bestPoint = fragmentHit;
        }
      }

      if (bestPoint) {
        const cameraDirection = new THREE.Vector3();
        camera.getWorldDirection(cameraDirection);
        const cameraAngle = (Math.atan2(cameraDirection.x, cameraDirection.y) * 180) / Math.PI;

        setPickedPoint({
          position: bestPoint,
          screenX: e.clientX,
          screenY: e.clientY,
          cameraAngle,
          // Only attach point-cloud attributes when the chosen hit was the
          // point-cloud (not a mesh): keeps the tooltip honest about what
          // the values describe.
          attributes: bestPoint === pcHit ? pcAttributes : undefined,
          pointCloudLayerId: bestPoint === pcHit ? pcLayerId : undefined,
        });
      }
    };

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('contextmenu', handleContextMenu);
    return () => {
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [camera, gl, meshTargets, pointCloudTargets, raycaster, setPickedPoint]);

  return null;
};
